import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import {
    useRepositorySuspenseQuery,
    RateGroupsGetAllParameters,
    useRepositoryMutation,
    GetRateParameters,
    Rate,
} from '@mews-commander/data-access';
import { WithRateGroupId } from '@mews-commander/types';
import { keyById } from '@mews/fp';
import { rateGroupsQueriesToInvalidate } from '../sidePanelTabs/queries';
import { createPaymentPolicyMapper } from '../../../../../rateGroups/queries/utils';

export const useLinkRateGroups = () =>
    useRepositoryMutation('portfolioCentralRateGroups', 'link', {
        invalidateQueries: rateGroupsQueriesToInvalidate,
    });

export const useLocalRateGroupWithPolicyExceptionsGet = ({ rateGroupIds, enterpriseIds }: RateGroupsGetAllParameters) =>
    useRepositorySuspenseQuery(
        'rateGroups',
        'getAll',
        {
            rateGroupIds,
            enterpriseIds,
            extent: {
                paymentPolicies: true,
            },
        },
        {
            select: ({ rateGroups, settlementRules, cancellationPolicies }) =>
                pipe(
                    rateGroups,
                    array.filterMap(option.fromNullable),
                    array.map(
                        flow(
                            createPaymentPolicyMapper({
                                cancellationPolicies: cancellationPolicies ?? [],
                                settlementRules: settlementRules ?? [],
                            }),
                            // Omit `settlementRules` and `cancellationPolicies` from the result
                            ({ settlementRules: srs, cancellationPolicies: cps, ...other }) => other
                        )
                    ),
                    array.filter(({ policyExceptions }) => array.isNonEmpty(policyExceptions))
                ),
        }
    );

export const useCentralRateGroupWithProfilesGet = ({ rateGroupId }: Partial<WithRateGroupId>) =>
    useRepositorySuspenseQuery(
        'portfolioCentralRateGroups',
        'getAll',
        {
            centralRateGroupIds: rateGroupId ? [rateGroupId] : null,
        },
        {
            select: ({ centralRateGroups, profiles, ...others }) => {
                const centralRateGroup = centralRateGroups[0];
                return centralRateGroup
                    ? {
                          ...centralRateGroup,
                          creatorProfile: profiles?.find((profile) => profile.id === centralRateGroup.creatorProfileId),
                          updaterProfile: profiles?.find((profile) => profile.id === centralRateGroup.updaterProfileId),
                          ...others,
                      }
                    : undefined;
            },
        }
    );

export const useRatesByIds = ({ enterpriseIds, rateIds }: GetRateParameters) =>
    useRepositorySuspenseQuery(
        'rates',
        'getAll',
        {
            enterpriseIds,
            rateIds: rateIds && array.isNonEmpty(rateIds) ? rateIds : null,
        },
        {
            select: (result): Record<string, Rate> => keyById<Rate>(result.rates),
        }
    );
